import React, {
  useState,
} from 'react';
import AddIcon from '@mui/icons-material/Add';
import clsx from 'clsx';
import ImageUploadMenu from 'pages/Dashboard/pages/Charts/pages/Details/components/ImageUploadMenu';
import {
  useGetAllowedFileTypes,
} from 'pages/Dashboard/hooks/media';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import useInsuranceAttachmentUpload from 'views/EMR/components/Insurance/InsuranceCard/useInsuranceAttachmentUpload';
import UnassociatedInsuranceCardSelect
  from 'views/EMR/components/Insurance/UnassociatedInsuranceCardSelect';
import {
  useAssociateInsuranceCard,
} from 'views/EMR/components/Insurance/hooks';
import {
  MultiMediaResponse,
} from 'dtos';
import {
  useNotify,
} from 'core/hooks';
import {
  insurancesEventChannel,
} from 'pages/Dashboard/services/eventChannels';

type Props = {
  insuranceId?: number;
  patientId?: number;
  onUploadSuccess: () => Promise<unknown> | void;
  disabled?: boolean;
};

export default function InsuranceAttachmentActions({
  insuranceId,
  patientId,
  onUploadSuccess,
  disabled = false,
}: Props) {
  const notify = useNotify();
  const [
    selectExistingAnchorEl,
    setSelectExistingAnchorEl,
  ] = useState<HTMLElement | null>(null);
  const [isSelectExistingOpen, setIsSelectExistingOpen] = useState(false);
  const allowedFileTypes = useGetAllowedFileTypes(MultimediaType.InsuranceCard);
  const {
    uploadFiles,
    isDisabled,
    isUploading,
  } = useInsuranceAttachmentUpload({
    insuranceId,
    patientId,
    onUploadSuccess,
  });
  const {
    mutateAsync: associateInsuranceCard,
    isPending: isAssociatingExistingCard,
  } = useAssociateInsuranceCard();
  const shouldDisableActions = disabled || isDisabled || isAssociatingExistingCard;

  const associateExistingCards = async (mediaList: MultiMediaResponse[]) => {
    const mediaIds = mediaList
      .map((media) => media.mediaId)
      .filter((mediaId): mediaId is number => (mediaId ?? 0) > 0);

    try {
      await Promise.all(mediaIds.map((mediaId) => associateInsuranceCard({
        insuranceId,
        patientMultiMediaId: mediaId,
      })));
      await onUploadSuccess();
      insurancesEventChannel.emit('onInsuranceCardAssociationUpdated', insuranceId ?? 0, mediaIds);
    } catch (error) {
      notify({
        variant: 'error',
        message: 'Unable to attach existing insurance card. Please try again.',
      });
      console.error(error);
      throw error;
    }
  };

  return (
    <>
      <div className={clsx({ 'pointer-events-none opacity-70': shouldDisableActions })}>
        <ImageUploadMenu
          onUpload={uploadFiles}
          isUploading={isUploading || isAssociatingExistingCard}
          allowedFileTypes={allowedFileTypes}
          triggerMode="menu"
          variant="outlined"
          color="primary"
          multiple
          classes={{
            popup: '!mt-1',
          }}
          trigger={(
            <button
              type="button"
              className={clsx(
                'flex h-16 w-16 shrink-0 items-center justify-center rounded-md',
                'border border-dashed border-gray-300 bg-transparent p-0',
                'text-blue-500 transition-colors hover:border-blue-400 hover:bg-blue-50/40',
                {
                  'cursor-not-allowed opacity-70':
                    isUploading || isAssociatingExistingCard,
                },
              )}
              disabled={isUploading || isAssociatingExistingCard}
              onClick={(event) => {
                setSelectExistingAnchorEl(event.currentTarget);
              }}
              aria-label="Add insurance attachment"
            >
              <AddIcon className="!h-6 !w-6 !text-inherit" />
            </button>
          )}
          extraMenuItems={[
            {
              id: 'select-existing-insurance-attachment',
              label: 'Select Unassociated Cards',
              onClick: () => {
                setIsSelectExistingOpen(true);
              },
            },
          ]}
          scanProps={{
            captureType: 'insurance-card',
            onCapturedFilesConfirmed: uploadFiles,
          }}
        />
      </div>
      {isSelectExistingOpen && (
        <UnassociatedInsuranceCardSelect
          patientId={patientId}
          anchorEl={selectExistingAnchorEl}
          onClose={() => {
            setIsSelectExistingOpen(false);
          }}
          onConfirm={associateExistingCards}
        />
      )}
    </>
  );
}
